import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { 
  BarChart3, Eye, Heart, MessageSquare, TrendingUp, TrendingDown,
  DollarSign, Package, Star, ArrowUpRight, Calendar, Activity, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: currency === 'XCG' ? 'AWG' : 'USD',
    minimumFractionDigits: 2,
  }).format(amount).replace('AWG', 'XCG ').replace('US$', '$');
};

const SellerAnalyticsPage = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/seller/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Analytics Available</h2>
          <p className="text-muted-foreground">Create some listings to see your analytics</p>
          <Link to="/create-listing" className="mt-4 inline-block px-6 py-2 bg-primary text-white rounded-lg">
            Create Listing
          </Link>
        </div>
      </div>
    );
  }

  const { summary, listings, views_trend, top_performing } = analytics;

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/my-listings')}
              className="p-2 rounded-lg bg-card border border-border hover:bg-muted transition-colors"
              data-testid="back-to-listings"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
                <BarChart3 className="w-7 h-7 text-primary" />
                Seller Analytics
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Track your listings performance</p>
            </div>
          </div>
          <div className="flex gap-2">
            {['7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range 
                    ? 'bg-primary text-white' 
                    : 'bg-card text-foreground hover:bg-primary/10'
                }`}
              >
                {range === '7d' ? '7D' : range === '30d' ? '30D' : '90D'}
              </button>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card rounded-xl p-5 border border-border" data-testid="stat-total-views">
            <div className="flex items-center justify-between mb-3">
              <Eye className="w-8 h-8 text-blue-500" />
              <span className="text-xs text-green-500 bg-green-500/10 px-2 py-1 rounded-full flex items-center">
                <TrendingUp className="w-3 h-3 mr-1" /> Active
              </span>
            </div>
            <div className="text-3xl font-bold text-foreground">{summary.total_views.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">Total Views</div>
          </div>

          <div className="bg-card rounded-xl p-5 border border-border" data-testid="stat-total-favorites">
            <div className="flex items-center justify-between mb-3">
              <Heart className="w-8 h-8 text-pink-500" />
            </div>
            <div className="text-3xl font-bold text-foreground">{summary.total_favorites.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">Total Favorites</div>
          </div>

          <div className="bg-card rounded-xl p-5 border border-border" data-testid="stat-total-inquiries">
            <div className="flex items-center justify-between mb-3">
              <MessageSquare className="w-8 h-8 text-teal-500" />
            </div>
            <div className="text-3xl font-bold text-foreground">{summary.total_inquiries.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">Total Inquiries</div>
          </div>

          <div className="bg-card rounded-xl p-5 border border-border" data-testid="stat-avg-rating">
            <div className="flex items-center justify-between mb-3">
              <Star className="w-8 h-8 text-amber-500" />
            </div>
            <div className="text-3xl font-bold text-foreground">{summary.avg_rating || '—'}</div>
            <div className="text-sm text-muted-foreground">{summary.total_reviews} Reviews</div>
          </div>
        </div>

        {/* Views Trend Chart */}
        {views_trend && views_trend.length > 0 && (
          <div className="bg-card rounded-xl p-6 border border-border mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Views Trend (Last 7 Days)
            </h2>
            <div className="flex items-end gap-2 h-40">
              {views_trend.map((day, index) => {
                const maxViews = Math.max(...views_trend.map(d => d.views));
                const height = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                return (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-primary/80 rounded-t-lg transition-all hover:bg-primary"
                      style={{ height: `${Math.max(height, 5)}%` }}
                      title={`${day.views} views`}
                    />
                    <span className="text-xs text-muted-foreground mt-2">
                      {new Date(day._id).toLocaleDateString('en', { weekday: 'short' })}
                    </span>
                    <span className="text-xs font-medium text-foreground">{day.views}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top Performing Listings */}
        {top_performing && top_performing.length > 0 && (
          <div className="bg-card rounded-xl p-6 border border-border mb-8">
            <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Top Performing Listings
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {top_performing.map((listing, index) => (
                <Link 
                  key={listing.id} 
                  to={`/listing/${listing.id}`}
                  className="p-4 bg-background rounded-lg border border-border hover:border-primary transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      index === 0 ? 'bg-amber-500 text-white' : 
                      index === 1 ? 'bg-slate-400 text-white' : 
                      'bg-orange-600 text-white'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="text-sm font-medium text-foreground truncate">{listing.title}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold text-foreground">{listing.views}</div>
                      <div className="text-xs text-muted-foreground">Views</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-foreground">{listing.favorites}</div>
                      <div className="text-xs text-muted-foreground">Favorites</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-foreground">{listing.inquiries}</div>
                      <div className="text-xs text-muted-foreground">Inquiries</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* All Listings Table */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              All Listings Performance
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Listing</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Views</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Favorites</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Inquiries</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Conversion</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Price</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {listings.map((listing) => (
                  <tr key={listing.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-6 py-4">
                      <Link to={`/listing/${listing.id}`} className="flex items-center gap-2 group">
                        <span className="font-medium text-foreground group-hover:text-primary transition-colors truncate max-w-xs">
                          {listing.title}
                        </span>
                        <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                      <span className="text-xs text-muted-foreground capitalize">{listing.listing_type}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Eye className="w-4 h-4 text-blue-500" />
                        <span className="font-medium text-foreground">{listing.views}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Heart className="w-4 h-4 text-pink-500" />
                        <span className="font-medium text-foreground">{listing.favorites}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <MessageSquare className="w-4 h-4 text-teal-500" />
                        <span className="font-medium text-foreground">{listing.inquiries}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`text-sm font-medium ${
                        listing.conversion_rate > 5 ? 'text-green-500' : 
                        listing.conversion_rate > 2 ? 'text-amber-500' : 
                        'text-muted-foreground'
                      }`}>
                        {listing.conversion_rate}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-foreground">
                      {formatCurrency(listing.price)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        listing.status === 'active' ? 'bg-green-500/10 text-green-500' :
                        listing.status === 'sold' ? 'bg-blue-500/10 text-blue-500' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {listing.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {listings.length === 0 && (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No listings yet. Create your first listing to see analytics.</p>
              <Link to="/create-listing" className="mt-4 inline-block px-6 py-2 bg-primary text-white rounded-lg">
                Create Listing
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerAnalyticsPage;
